import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";

import DateFnsUtils from "@date-io/date-fns";
import {
  BaseTextFieldProps,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputBaseComponentProps,
  OutlinedInputProps,
  TextField,
  TextFieldProps,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import classNames from "classnames";
import { format, isValid, parseISO } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { Calendar } from "react-date-range";
import { FiCalendar } from "react-icons/fi";

import { getBrandCustom } from "../../../utils/getCustomColor";
import { StyledMenu } from "./DateRange/styles";
import useStyles from "./styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

export enum CodeError {
  NOT_AVAILABLE = "NOT_AVAILABLE",
  STATUS_INACTIVE = "STATUS_INACTIVE",
  FORBIDDEN = "FORBIDDEN",
  UNKNOWN = "unknown",
  INVALID_CSV = "INVALID_CSV",
}

export enum TypeError {
  SUCCESS = "success",
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
  ERROR_WITHOUT_TOAST = "ERROR_WITHOUT_TOAST",
}

export interface Error {
  type?: TypeError;
  statusCode?: number;
  message?: string;
  field?: string;
  error?: CodeError;
  email?: string;
  purchase_order_id?: string;
}

export interface InputProps extends BaseTextFieldProps {
  name?: string;
  type?: React.InputHTMLAttributes<unknown>["type"];
  accept?: string;
  label?: React.ReactNode;
  error?: boolean;
  errorMessage?: string;
  errorMessageClass?: string;
  errorResponse?: Error;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  maxLength?: number;
  onChange?: OutlinedInputProps["onChange"];
  onBlur?: OutlinedInputProps["onBlur"];
  onFocus?: OutlinedInputProps["onFocus"];
  formControlStyle?: CSSProperties;
  min?: string | number;
  max?: string | number;
  step?: number;
  inputProps?: InputBaseComponentProps;
  bordercolor?: string;
  formControlClassName?: string;
  labelClassName?: string;
  disableFuture?: boolean;
  disablePast?: boolean;
  inputTextAlign?: "start" | "center" | "end";
  errorBreakLine?: boolean;
  "data-cy"?: string;
  setIsDateValid?: (value: boolean) => void;
}

const TextFieldCustomized = withStyles({
  root: {
    "& input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active":
      {
        "-webkit-box-shadow": "0 0 0 30px white inset !important",
        "-webkit-text-fill-color": "#667085",
      },
    "& .MuiOutlinedInput-root": {
      backgroundColor: (props: { disabled?: boolean }) => (props?.disabled ? "#F9FAFB" : "#FFFFFF"),
      height: (props: { multiline?: boolean }) => (!props?.multiline ? "40px" : undefined),
      borderRadius: "8px",
      fontFamily: "Manrope",
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "24px",
      color: "#667085",
      "& fieldset": {
        borderColor: (props: { error?: boolean; bordercolor?: string }) =>
          props?.error ? props?.bordercolor : "#C4C4C4",
      },
      "&:hover fieldset": {
        borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
          !props?.disabled ? props?.bordercolor : "#C4C4C4",
      },
      "&.Mui-focused fieldset": {
        borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
          !props?.disabled ? props?.bordercolor : "#C4C4C4",
      },
    },
  },
})(TextField);

const FormControlCustomized = withStyles(() => ({
  root: {
    display: "flex",
    justifyContent: "flex-end",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "&:active .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
  },
}))(FormControl);

const TextFieldComponent = (fieldProps: React.PropsWithChildren<any>) => (
  <TextFieldCustomized
    {...fieldProps}
    placeholder="dd/mm/yyyy"
    helperText=""
    fullWidth
    variant="outlined"
  />
);

const Input = (props: InputProps) => {
  const {
    label,
    error,
    errorMessage,
    errorMessageClass,
    disabled,
    fullWidth,
    maxLength,
    min,
    max,
    step,
    leftIcon,
    rightIcon,
    formControlClassName,
    formControlStyle,
    labelClassName,
    defaultValue,
    value,
    type,
    onChange,
    disableFuture,
    disablePast,
    inputTextAlign,
    required,
    errorBreakLine = true,
    errorResponse,
    setIsDateValid,
    ...rest
  } = props;

  const divRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<SVGElement | null>(null);
  const open: boolean = !!anchorEl;

  const [dateIsInValid, setDateIsInValid] = useState<boolean>(false);
  const [dateValue, setDateValue] = useState<any>("");
  const [dateInputValue, setDateInputValue] = useState<any>(null);
  const [date, setDate] = useState<Date>();

  const classes = useStyles({ disabled });

  useEffect(() => {
    if (type === "date" && value && typeof value === "string") {
      const valueSplitted = value.split("-");

      if (valueSplitted && valueSplitted.length === 3) {
        const year = valueSplitted[0];
        const month = valueSplitted[1];
        const day = valueSplitted[2];

        setDateValue(`${day}/${month}/${year}`);
      }
    } else {
      setDateValue("");
      setDateInputValue(null);
    }
  }, [value]);

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  const renderLabel = () => {
    if (label) {
      return (
        <span className={classNames(classes.label, labelClassName)}>
          {label}
          {renderRequiredLabel()}
        </span>
      );
    }

    return null;
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText
          className={classNames(classes.formHelper, errorMessageClass)}
          data-cy="errors"
          style={!errorBreakLine ? { whiteSpace: "nowrap" } : undefined}
        >
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }
    if (errorResponse && errorResponse?.field === rest.name) {
      return (
        <FormHelperText
          className={classes.formHelper}
          data-cy="errors"
          style={!errorBreakLine ? { whiteSpace: "nowrap" } : undefined}
        >
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorResponse?.message}
        </FormHelperText>
      );
    }
  };

  const renderInputType = () => {
    switch (type) {
      case "date":
        const extraAttrs = {
          "data-cy": rest["data-cy"] as string,
        };

        return (
          <div ref={divRef}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                name={rest?.name}
                disabled={disabled}
                format="dd/MM/yyyy"
                minDate={min ? new Date(min) : undefined}
                value={dateInputValue}
                slotProps={{
                  textField(states) {
                    return {
                      fullWidth,
                      placeholder: "dd/mm/yyyy",
                      error: error || dateIsInValid,
                      disabled,
                      ...states,
                      ...extraAttrs,
                    };
                  },
                }}
                disableFuture={disableFuture}
                disablePast={disablePast}
                open={false}
                onChange={(value, _) => {
                  setDateInputValue(value);
                  try {
                    if (setIsDateValid) setIsDateValid(true);
                    else setDateIsInValid(true);
                    setDateValue(value || "");
                    if (value) {
                      const caracteres = value.replace(/_/g, "");

                      if (caracteres && caracteres?.length === 10) {
                        const valueSplitted = value.split("/");
                        const year = valueSplitted[2];
                        const month = valueSplitted[1];
                        const day = valueSplitted[0];
                        const dateFormatted = `${year}-${month}-${day}`;

                        if (isValid(parseISO(dateFormatted))) {
                          if (setIsDateValid) setIsDateValid(false);
                          else setDateIsInValid(false);

                          setDate(
                            zonedTimeToUtc(
                              dateFormatted,
                              Intl.DateTimeFormat().resolvedOptions().timeZone
                            )
                          );

                          if (onChange)
                            onChange({
                              target: {
                                value: dateFormatted,
                              },
                            } as unknown as React.ChangeEvent<HTMLTextAreaElement>);
                        }
                      } else if (setIsDateValid) setIsDateValid(true);
                      else setDateIsInValid(true);
                    } else {
                      if (onChange)
                        onChange({
                          target: {
                            value: null,
                          },
                        } as unknown as React.ChangeEvent<HTMLTextAreaElement>);
                      if (setIsDateValid) setIsDateValid(false);
                      else setDateIsInValid(false);
                    }
                  } catch (err) {
                    if (setIsDateValid) setIsDateValid(true);
                    else setDateIsInValid(true);
                  }
                }}
              />
            </LocalizationProvider>
            <StyledMenu
              id="date-range-picker"
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <Calendar
                showDateDisplay={false}
                preventSnapRefocus
                minDate={min ? new Date(min) : undefined}
                maxDate={max ? new Date(max) : undefined}
                date={date}
                dateDisplayFormat="dd/MM/yyyy"
                onChange={(date: any) => {
                  setDateIsInValid(false);
                  setDate(date);
                  setDateValue(format(date, "dd/MM/yyyy"));

                  if (onChange)
                    onChange({
                      target: {
                        value: format(date, "yyy-MM-dd"),
                      },
                    } as unknown as React.ChangeEvent<HTMLTextAreaElement>);

                  setAnchorEl(null);
                }}
              />
            </StyledMenu>
          </div>
        );
      default:
        return (
          <TextFieldCustomized
            disabled={disabled}
            fullWidth={fullWidth}
            defaultValue={!value && value !== "" && defaultValue ? defaultValue : undefined}
            value={value || ""}
            variant="outlined"
            inputProps={{
              maxLength,
              min,
              max,
              step,
              style: {
                textAlign: rest.style?.textAlign ? rest.style?.textAlign : inputTextAlign,
              },
            }}
            onKeyDown={(event) => type === "number" && event.key === "e" && event.preventDefault()}
            onChange={onChange}
            InputProps={{
              startAdornment: leftIcon ? (
                <InputAdornment position="start">{leftIcon}</InputAdornment>
              ) : undefined,
              endAdornment: rightIcon ? (
                <InputAdornment position="end">{rightIcon}</InputAdornment>
              ) : undefined,
            }}
            error={
              error || (rest.name && errorResponse ? errorResponse?.field === rest.name : false)
            }
            type={type}
            {...rest}
          />
        );
    }
  };

  return (
    <FormControlCustomized
      fullWidth={fullWidth}
      style={formControlStyle}
      className={formControlClassName}
      disabled={disabled}
      bordercolor={
        error || dateIsInValid || (errorResponse && errorResponse?.field === rest.name)
          ? "#D92D20"
          : getBrandCustom("primaryColor", "#308CDB")
      }
    >
      {renderLabel()}
      {renderInputType()}
      {renderError()}
    </FormControlCustomized>
  );
};

export default Input;
